'use client';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { QueryApi } from '@/features/query/api';
import { MenuItemProvider } from '@/shared/ui/providers/menu-item-provider';
import { normalizeId, resolveInjections, resolveOverride } from './helpers';
import { MenuItemsData } from './types';

type Props = Omit<MenuItemsData, 'data'>;

export const MenuItemsFor = ({ children, filters, limit, injections, overrides }: Props) => {
    const { data: allItems = [] } = useQuery(QueryApi.MenuList.queries.AggregatedMenuItems);

    const filteredItems = useMemo(() => {
        let items = [...allItems];
        if (filters?.categoryIds?.length) {
            const allowed = new Set(filters.categoryIds);
            items = items.filter((i) => allowed.has(i.categoryId));
        }
        if (filters?.availableInVenueId) {
            items = items.filter((i) => i.availableInVenueIds.includes(filters.availableInVenueId!));
        }
        items.sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));
        const mergedLimit = typeof limit === 'number' ? limit : (filters?.limit ?? null);
        return typeof mergedLimit === 'number' ? items.slice(0, Math.max(0, mergedLimit)) : items;
    }, [allItems, filters, limit]);

    return (
        <>
            {filteredItems.flatMap((item, index) => {
                const itemId = normalizeId(item.itemId);
                const hash = normalizeId(item.hash);
                const override = resolveOverride(overrides, itemId, hash, index);
                const itemChildren = override?.children ?? children;
                const before = resolveInjections(injections, itemId, hash, index, 'before');
                const after = resolveInjections(injections, itemId, hash, index, 'after');

                return [
                    ...before,
                    <MenuItemProvider
                        key={item.hash ?? itemId ?? `${index}`}
                        value={{ data: item, index, itemsIndex: undefined }}
                    >
                        {itemChildren}
                    </MenuItemProvider>,
                    ...after,
                ];
            })}
        </>
    );
};
